import React, { useState, useEffect, useMemo } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory, exportCategories } from '../../lib/api';
import { downloadBlob } from '../../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Tags,
  Plus,
  Edit,
  Trash2,
  Loader2,
  AlertCircle,
  FolderTree,
  ChevronRight,
  FolderOpen,
  Folder,
  Download,
  Printer
} from 'lucide-react';

const Categories = () => {
  const { isAdmin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ category_name: '', category_code: '', parent_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportCategories();
      downloadBlob(res.data, `categories_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Categories exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const [expandedParents, setExpandedParents] = useState(new Set());

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getCategories();
      setCategories(res.data);
      // Auto-expand all parents
      const parentIds = new Set(res.data.filter(c => !c.parent_id).map(c => c.id));
      setExpandedParents(parentIds);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const parentCategories = useMemo(() => categories.filter(c => !c.parent_id), [categories]);
  const getChildren = (parentId) => categories.filter(c => c.parent_id === parentId);

  const toggleParent = (id) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openCreateDialog = (parentId = '') => {
    setEditingCategory(null);
    setFormData({ category_name: '', category_code: '', parent_id: parentId });
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      category_name: category.category_name,
      category_code: category.category_code,
      parent_id: category.parent_id || ''
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.category_name.trim() || !formData.category_code.trim()) {
      setError('Name and Code are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        category_name: formData.category_name.trim(),
        category_code: formData.category_code.trim().toUpperCase(),
        parent_id: formData.parent_id || null
      };
      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
        toast.success('Category updated');
      } else {
        await createCategory(payload);
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteCategory(itemToDelete.id);
      toast.success('Category deleted');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="categories-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Category Management</h1>
          <p className="text-[#6B7280]">Organize items with parent categories and subcategories</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={handleExport} disabled={exporting} className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg" data-testid="export-categories-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />} Export Excel
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]" data-testid="print-categories-btn">
            <Printer size={18} /> Print
          </Button>
          <Button
            onClick={() => openCreateDialog('')}
            className="gap-2 bg-[#0776BD] hover:bg-[#055A92] text-white rounded-lg"
            data-testid="add-parent-category-btn"
          >
            <Plus size={18} /> Add Main Category
          </Button>
        </div>
      </div>

      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <FolderTree size={20} className="text-[#0776BD]" />
            Categories ({categories.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : parentCategories.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <Tags size={48} className="mx-auto mb-4 opacity-50" />
              <p>No categories found</p>
              <p className="text-sm">Click "Add Main Category" to create one</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F6F7]">
                  <TableHead className="font-semibold text-[#1F2937]">Category</TableHead>
                  <TableHead className="font-semibold text-[#1F2937]">Code</TableHead>
                  <TableHead className="font-semibold text-[#1F2937]">Type</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parentCategories.map((parent) => {
                  const children = getChildren(parent.id);
                  const isExpanded = expandedParents.has(parent.id);
                  return (
                    <React.Fragment key={parent.id}>
                      <TableRow
                        className="hover:bg-[#F5F6F7] bg-blue-50/40 cursor-pointer"
                        onClick={() => toggleParent(parent.id)}
                        data-testid={`parent-category-row-${parent.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <ChevronRight size={16} className={`text-[#0776BD] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            {isExpanded ? <FolderOpen size={18} className="text-[#0776BD]" /> : <Folder size={18} className="text-[#0776BD]" />}
                            <span className="font-semibold text-[#1F2937]">{parent.category_name}</span>
                            <Badge className="bg-[#E6F2FA] text-[#0776BD] border-0 text-xs ml-1">{children.length} sub</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-[#0776BD] font-semibold">{parent.category_code}</TableCell>
                        <TableCell>
                          <Badge className="bg-[#0776BD] text-white border-0">Parent</Badge>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => openCreateDialog(parent.id)}
                              className="hover:bg-[#E6F2FA] text-[#0776BD] gap-1 text-xs"
                              data-testid={`add-sub-${parent.id}`}
                            >
                              <Plus size={14} /> Sub
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => openEditDialog(parent)}
                              className="hover:bg-[#E6F2FA] text-[#0776BD]"
                              data-testid={`edit-category-${parent.id}`}
                            >
                              <Edit size={16} />
                            </Button>
                            {isAdmin() && (
                              <Button
                                variant="ghost" size="icon"
                                onClick={() => { setItemToDelete(parent); setDeleteDialogOpen(true); }}
                                className="hover:bg-red-50 text-red-500"
                                data-testid={`delete-category-${parent.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && children.map((child) => (
                        <TableRow key={child.id} className="hover:bg-[#F5F6F7]" data-testid={`sub-category-row-${child.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2 pl-10">
                              <Tags size={14} className="text-[#6B7280]" />
                              <span className="text-[#1F2937]">{child.category_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm text-[#0776BD]">{child.category_code}</TableCell>
                          <TableCell>
                            <Badge className="bg-gray-100 text-gray-600 border-0">Subcategory</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost" size="icon"
                                onClick={() => openEditDialog(child)}
                                className="hover:bg-[#E6F2FA] text-[#0776BD]"
                                data-testid={`edit-category-${child.id}`}
                              >
                                <Edit size={16} />
                              </Button>
                              {isAdmin() && (
                                <Button
                                  variant="ghost" size="icon"
                                  onClick={() => { setItemToDelete(child); setDeleteDialogOpen(true); }}
                                  className="hover:bg-red-50 text-red-500"
                                  data-testid={`delete-category-${child.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">
              {editingCategory ? 'Edit Category' : (formData.parent_id ? 'Add Subcategory' : 'Add Main Category')}
            </DialogTitle>
          </DialogHeader>

          {error && (
            <Alert variant="destructive" className="rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {/* Parent selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Type</Label>
              <Select
                value={formData.parent_id || 'root'}
                onValueChange={(val) => setFormData({ ...formData, parent_id: val === 'root' ? '' : val })}
              >
                <SelectTrigger className="h-11 rounded-lg border-gray-300" data-testid="category-parent-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">Main Category (no parent)</SelectItem>
                  {parentCategories
                    .filter(p => p.id !== editingCategory?.id)
                    .map(p => (
                      <SelectItem key={p.id} value={p.id}>Subcategory of {p.category_name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Category Name *</Label>
              <Input
                value={formData.category_name}
                onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
                placeholder={formData.parent_id ? 'e.g., Switches' : 'e.g., Electrical'}
                className="h-11 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="category-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Code *</Label>
              <Input
                value={formData.category_code}
                onChange={(e) => setFormData({ ...formData, category_code: e.target.value.toUpperCase() })}
                placeholder={formData.parent_id ? 'e.g., SW' : 'e.g., ELEC'}
                maxLength={5}
                className="h-11 rounded-lg border-gray-300 focus:border-[#0776BD] font-mono"
                data-testid="category-code-input"
              />
              <p className="text-xs text-[#6B7280]">Used in internal item codes. Max 5 characters.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-category-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">Delete Category</DialogTitle>
          </DialogHeader>
          <p className="text-[#6B7280]">
            Are you sure you want to delete <strong>"{itemToDelete?.category_name}"</strong>?
            {!itemToDelete?.parent_id && ' This is a main category. You must remove all subcategories first.'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="rounded-lg" data-testid="confirm-delete-category-btn">
              {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Categories;
